import React, { useState } from 'react'
import { Component, LocaleCode, LocalizedText } from '../types/component'
import { getLocalizedText } from '../utils/localization'

interface TestimonialsProps {
  component: Component
  locale: LocaleCode
  storeId: string
  apiUrl: string
  style: React.CSSProperties
}

interface Testimonial {
  id: string
  name: string | LocalizedText
  role?: string | LocalizedText
  company?: string | LocalizedText
  avatar?: string
  rating?: number
  text: string | LocalizedText
}

export function Testimonials({ component, locale, style }: TestimonialsProps) {
  const config = component.config as any
  const layout = config.layout || 'grid'
  const columns = config.columns || 3
  const showRatings = config.showRatings !== false
  const showAvatars = config.showAvatars !== false
  const testimonials: Testimonial[] = config.testimonials || [
    {
      id: '1',
      name: 'Sarah Johnson',
      role: 'Marketing Director',
      company: 'Tech Corp',
      rating: 5,
      text: 'This product has completely transformed how we work. Highly recommended!',
    },
    {
      id: '2',
      name: 'Michael Chen',
      role: 'CEO',
      company: 'StartupXYZ',
      rating: 5,
      text: 'Outstanding quality and excellent customer service. Worth every penny.',
    },
    {
      id: '3',
      name: 'Emily Davis',
      role: 'Product Manager',
      rating: 4,
      text: 'Great experience overall. The team was professional and responsive.',
    },
  ]

  const [currentSlide, setCurrentSlide] = useState(0)

  const containerStyle: React.CSSProperties = {
    ...style,
    padding: '16px',
    backgroundColor: style.backgroundColor || '#ffffff',
  }

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns:
      layout === 'grid'
        ? columns === 2
          ? 'repeat(2, 1fr)'
          : columns === 3
            ? 'repeat(3, 1fr)'
            : '1fr'
        : '1fr',
    gap: '24px',
  }

  const listStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  }

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1))
  }

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1))
  }

  const renderStars = (rating: number) => {
    return (
      <div style={{ display: 'flex', gap: '4px' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            style={{ color: star <= rating ? '#fbbf24' : '#d1d5db', fontSize: '18px' }}
          >
            ★
          </span>
        ))}
      </div>
    )
  }

  const renderTestimonialCard = (testimonial: Testimonial) => {
    const cardStyle: React.CSSProperties = {
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '24px',
      backgroundColor: '#ffffff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      transition: 'box-shadow 0.2s',
    }

    return (
      <div
        key={testimonial.id}
        style={cardStyle}
        onMouseEnter={(e) =>
          (e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)')
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)')
        }
      >
        {showRatings && testimonial.rating && (
          <div style={{ marginBottom: '16px' }}>
            {renderStars(testimonial.rating)}
          </div>
        )}

        <p
          style={{
            color: '#374151',
            marginBottom: '16px',
            fontStyle: 'italic',
            lineHeight: '1.6',
          }}
        >
          "{getLocalizedText(testimonial.text, locale)}"
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {showAvatars && (
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: '#e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {testimonial.avatar ? (
                <img
                  src={testimonial.avatar}
                  alt={getLocalizedText(testimonial.name, locale)}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <span style={{ color: '#6b7280', fontWeight: '500' }}>
                  {getLocalizedText(testimonial.name, locale)
                    .split(' ')
                    .map((n) => n[0])
                    .join('')}
                </span>
              )}
            </div>
          )}
          <div>
            <p style={{ fontWeight: '600', color: '#111827' }}>
              {getLocalizedText(testimonial.name, locale)}
            </p>
            {testimonial.role && (
              <p style={{ fontSize: '14px', color: '#6b7280' }}>
                {getLocalizedText(testimonial.role, locale)}
                {testimonial.company &&
                  ` at ${getLocalizedText(testimonial.company, locale)}`}
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (layout === 'carousel') {
    return (
      <div style={containerStyle}>
        <div style={{ maxWidth: '1024px', margin: '0 auto' }}>
          <div style={{ position: 'relative' }}>
            {renderTestimonialCard(testimonials[currentSlide])}

            {testimonials.length > 1 && (
              <>
                <button
                  onClick={handlePrevSlide}
                  style={{
                    position: 'absolute',
                    left: '-48px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.boxShadow =
                      '0 10px 15px rgba(0,0,0,0.1)')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)')
                  }
                  aria-label="Previous testimonial"
                >
                  ←
                </button>
                <button
                  onClick={handleNextSlide}
                  style={{
                    position: 'absolute',
                    right: '-48px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.boxShadow =
                      '0 10px 15px rgba(0,0,0,0.1)')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)')
                  }
                  aria-label="Next testimonial"
                >
                  →
                </button>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '8px',
                    marginTop: '24px',
                  }}
                >
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        border: 'none',
                        backgroundColor:
                          index === currentSlide ? '#3b82f6' : '#d1d5db',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                      }}
                      aria-label={`Go to testimonial ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={containerStyle}>
      <div style={layout === 'grid' ? gridStyle : listStyle}>
        {testimonials.map(renderTestimonialCard)}
      </div>
    </div>
  )
}
